/**
 * Controls Bar Component
 * Top bar with practice mode switch, stats badge, and settings button
 */

import { useEffect, useRef } from 'react'
import PracticeModeSwitch from './PracticeModeSwitch'
import StatsBadge from './StatsBadge'
import type { Stats } from '../../types'

interface ControlsBarProps {
  stats: Stats
  currentStreak: number
  onSettingsToggle: () => void
  settingsOpen: boolean
}

export default function ControlsBar({
  stats,
  currentStreak,
  onSettingsToggle,
  settingsOpen,
}: ControlsBarProps) {
  const toggleLabel = settingsOpen ? 'Close practice settings' : 'Open practice settings'
  const settingsButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const updateSettingsOffset = () => {
      const rect = settingsButtonRef.current?.getBoundingClientRect()
      if (!rect) return
      const offset = Math.max(12, window.innerWidth - rect.right)
      document.documentElement.style.setProperty('--practice-settings-offset', `${offset}px`)
    }

    updateSettingsOffset()
    window.addEventListener('resize', updateSettingsOffset)
    return () => {
      window.removeEventListener('resize', updateSettingsOffset)
      document.documentElement.style.removeProperty('--practice-settings-offset')
    }
  }, [])

  useEffect(() => {
    const rect = settingsButtonRef.current?.getBoundingClientRect()
    if (!rect) return
    const offset = Math.max(0, window.innerWidth - rect.right)
    document.documentElement.style.setProperty('--practice-settings-offset', `${offset}px`)
  }, [settingsOpen])

  return (
    <section className="controls">
      <div className="controls__bar">
        <PracticeModeSwitch />
        <StatsBadge stats={stats} currentStreak={currentStreak} />
        <button
          className={`settings-toggle ${settingsOpen ? 'settings-toggle--open' : ''}`}
          id="settingsToggle"
          type="button"
          aria-expanded={settingsOpen}
          aria-controls="settingsPanel"
          onClick={onSettingsToggle}
          ref={settingsButtonRef}
        >
          <span className="settings-toggle__bar" aria-hidden="true"></span>
          <span className="settings-toggle__bar" aria-hidden="true"></span>
          <span className="settings-toggle__bar" aria-hidden="true"></span>
          <span className="sr-only">{toggleLabel}</span>
        </button>
      </div>
    </section>
  )
}
